import { CheckmarkFilled, DangerFilled, TooltipFilled, WarningFilled } from '@icon';
import { HilbiSymbol } from '@illustrations';
import React, { SVGProps } from 'react';
import styled, { useTheme } from 'styled-components';
import { TColorsOptions, TMessageBoxSize, TMessageBoxVariant } from './message-box.types';

const clsPrefix = 'ui-message-box';

export const msgBoxCls = {
  wrapper: clsPrefix,
  icon: `${clsPrefix}-icon`,
  title: `${clsPrefix}-title`,
  description: `${clsPrefix}-description`,
  caption: `${clsPrefix}-caption`,
  footer: `${clsPrefix}-footer`,
} as const;

export const SMALL_ICON_MAP: Record<
  TMessageBoxVariant,
  (props: SVGProps<SVGSVGElement>) => JSX.Element
> = {
  info: TooltipFilled,
  success: CheckmarkFilled,
  danger: DangerFilled,
  warning: WarningFilled,
};

export const LARGE_ICON_MAP: Record<
  TMessageBoxVariant,
  (props: SVGProps<SVGSVGElement>) => JSX.Element
> = {
  info: HilbiSymbol,
  success: HilbiSymbol,
  danger: HilbiSymbol,
  warning: HilbiSymbol,
};

export const useBoxColors = () => {
  const theme = useTheme();

  const colors = React.useMemo<TColorsOptions>(
    () => ({
      info: {
        icon: theme.color.solid.info,
        background: theme.color.surface.info,
        text: theme.color.tint.onInfo,
      },
      success: {
        icon: theme.color.solid.success,
        background: theme.color.surface.success,
        text: theme.color.tint.onSuccess,
      },
      danger: {
        icon: theme.color.solid.error,
        background: theme.color.surface.error,
        text: theme.color.tint.error,
      },
      warning: {
        icon: theme.color.solid.warning,
        background: theme.color.surface.warning,
        text: theme.color.tint.onWarning,
      },
    }),
    [theme],
  );

  return colors;
};

export const MessageBoxSC = styled.div<{ size: TMessageBoxSize }>`
  display: flex;
  flex-direction: row;
  padding: ${({ size }) => (size === 'large' ? '16px' : '8px 16px')};
`;

export const TextWrapper = styled.div<{ size: TMessageBoxSize }>`
  flex: 1;
  margin-left: ${({ size }) => (size === 'large' ? '16px' : '8px')};

  .${msgBoxCls.title} {
    ${({ theme }) => theme.mappedTypography.h6.default};

    &-large {
      padding-top: 4px;
    }
  }

  .${msgBoxCls.description} {
    margin-top: 4px;
    ${({ theme }) => theme.mappedTypography.body2.default};
    color: ${({ theme }) => theme.color.tint.secondary};
  }

  .${msgBoxCls.caption} {
    ${({ theme }) => theme.mappedTypography.body2.default};
  }

  .${msgBoxCls.footer} {
    margin-top: 16px;

    display: flex;
    flex-direction: row;
    gap: 16px;
  }
`;
